import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getUserData, updateUser } from 'redux/reducers/users'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'
import { Alert } from '@cmsgov/ds-medicare-gov'

import { Button } from 'components/Button/Button'
import { MyDialog } from 'components/MyDialog'
import { medicareApiClient } from 'clients/medicareApiClient'
import { ILoadingStatus } from 'models/LoadingStatus'
import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { useMountedState } from 'hooks/useMountedState'
import { shallowDiff } from 'utils/object-helpers'
import { zodResolver } from '@hookform/resolvers/zod'
import { createSchema, MedicareYouHandbookForm } from 'models/forms/MedicareYouHandbookForm'
import { MedicareYouHandbookModalUneditable } from '.'
import { WarningContinue } from '../SharedModalComponents/WarningContinue'
import { Pill } from 'components/Pill/Pill'
import { useModalCloseEvent } from 'components/MyDialog/useModalCloseEvent'
import { Logger } from 'utils/logger'
import { EHandbookMedia } from '@cms-max/medicare-api-sdk'
import { MySpinner } from 'components/MySpinner/MySpinner'

export type EditHandbookServerErrors = {
  basic?: boolean
  emailFail?: boolean
  emailSuccessPrefFail?: boolean
}
export type MedicareYouHandbookModalProps = {
  onClose: () => void,
}

export const MedicareYouHandbookModal: FC<MedicareYouHandbookModalProps> = ({
  onClose
}) => {
  const { t } = useTranslation(['medicareYouHandbook', 'accountSettingsEmail'])
  const userData = useSelector(getUserData)
  const hasEmail = Boolean(userData?.user?.email)
  const existingValues = userData?.user?.eHandbookPreferences
  const existingEmail = userData.user?.email

  const isPaperOrElectronic = (existingValues?.media === EHandbookMedia.Paper) || (existingValues?.media === EHandbookMedia.Electronic)
  const [warningDisplayAcceptedAltFormat, setWarningDisplayAcceptedAltFormat] = useState(false)

  const [serverErrors, setServerErrors] = useState<
    Partial<EditHandbookServerErrors>
  >({})

  const defaultValuesObject = useMemo(() => {
    return {
      isEmailPreferred: userData.user?.eHandbookPreferences?.isEmailPreferred,
      language: userData.user?.eHandbookPreferences?.language,
      media: userData.user?.eHandbookPreferences?.media,
      email: existingEmail ?? '',
      confirmEmail: existingEmail ?? ''
    }
  }, [userData, existingEmail])

  const schema = useMemo(() => createSchema(t), [t])
  const methods = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: defaultValuesObject,
    resolver: zodResolver(schema)
  })

  const { formState, watch, setValue, clearErrors } = methods
  const watchMedia = watch('media')

  const [emailsReceivedErrors, setEmailsReceivedErrors] = useState(false)
  const formErrors = methods.formState.errors
  useEffect(() => {
    if (formErrors.confirmEmail || formErrors.email) {
      setEmailsReceivedErrors(true)
    }
  }, [formErrors.email, formErrors.confirmEmail, setEmailsReceivedErrors])

  useEffect(() => {
    if (watchMedia === EHandbookMedia.Electronic) {
      const setValueConfig = {
        shouldValidate: Boolean(existingEmail)
      }
      setEmailsReceivedErrors(false)
      setValue('email', existingEmail ?? '', setValueConfig)
      setValue('confirmEmail', existingEmail ?? '', setValueConfig)
    } else {
      setValue('email', '')
      setValue('confirmEmail', '')
      clearErrors(['email', 'confirmEmail'])
      setEmailsReceivedErrors(false)
    }
  }, [watchMedia, existingEmail, setValue, clearErrors, setEmailsReceivedErrors])

  const dispatch = useDispatch()
  const [loadingState, setLoadingState] =
    useMountedState<ILoadingStatus>('standby')

  const handleClose = useModalCloseEvent('accountSettings', 'accountsettings_handbookclose_click', onClose)

  if (!existingValues) {
    return <MedicareYouHandbookModalUneditable onClose={onClose} />
  }

  const onSubmit: SubmitHandler<MedicareYouHandbookForm> = async (formData) => {
    setLoadingState('loading')
    setServerErrors({})

    const formDataWithoutEmail = {
      language: formData.language,
      media: formData.media
    }
    const existingValuesWithoutIsEmailPreferred = {
      language: existingValues.language,
      media: existingValues.media
    }

    const diff = shallowDiff(existingValuesWithoutIsEmailPreferred, formDataWithoutEmail)
    const payload = {
      ...(diff.language ? { language: diff.language } : {}),
      ...(diff.media ? { isEmailPreferred: diff.media === EHandbookMedia.Electronic } : {})
    }

    const needUpdateEmail = Boolean(formData.email && userData.user?.email !== formData.email)

    // Email
    if (formData.email && needUpdateEmail) {
      try {
        await medicareApiClient.asyncUpdateBeneficiaryEmail({
          email: formData.email
        })
        dispatch(
          updateUser({
            email: formData.email
          })
        )
      } catch (error) {
        Logger.error('Error updating email', error)
        setServerErrors({ emailFail: true })
        setLoadingState('error')
        // Do not continue
        return
      }
    }

    try {
      await medicareApiClient.asyncUpdateBeneficiary({ eHandbookPreferences: payload })
      dispatch(updateUser({
        eHandbookPreferences: { ...existingValues, ...payload, media: formData.media as EHandbookMedia }
      }))

      setLoadingState('finished')

      // Close modal
      onClose?.()
    } catch (error) {
      if (needUpdateEmail) {
        setServerErrors({ emailSuccessPrefFail: true })
      } else {
        setServerErrors({ basic: true })
      }

      // set error state and display the alert on the modal
      setLoadingState('error')
    }
  }

  const defaultChoices = [
    {
      label: t('format.electronically'),
      value: EHandbookMedia.Electronic
    },
    {
      label: t('format.paper'),
      value: EHandbookMedia.Paper
    }
  ]
  const alternateChoices = [
    ...defaultChoices,
    {
      label:
        userData.user?.eHandbookPreferences?.media ?? '',
      value:
        userData.user?.eHandbookPreferences?.media ?? ''
    }
  ]

  const mediaChoices = (
    (
      userData.user?.eHandbookPreferences?.media !== EHandbookMedia.Electronic &&
      userData.user?.eHandbookPreferences?.media !== EHandbookMedia.Paper
    )
      ? alternateChoices
      : defaultChoices
  )

  return (
    <MyDialog
      heading={t('heading')}
      containerId="medicare-my-medicare-you-handbook"
      closeButtonAriaLabel={t('Close')}
      onClose={handleClose}
    >
      <>
        {!(existingValues.isEmailPreferred || isPaperOrElectronic) && !warningDisplayAcceptedAltFormat
          ? <>
              <p>{t('warnings.altformat.paragraph')}</p>
              <Alert variation='warn'>{t('warnings.altformat.alert')}</Alert>
              <h2 className='ds-text-heading--xl'>
              {t('currentFormat')}
              </h2>
              <Pill>{t(`${existingValues.media}`)}</Pill>
              <WarningContinue
                onClose={onClose}
                setWarningDisplayAcceptedAltFormat={setWarningDisplayAcceptedAltFormat}
              />
            </>
          : <>
            <p className='my-u-pb--8'>{t('subtitle-paragraph')}</p>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Alert hideIcon={true} className="my-u-mb--14">
            <p className="ds-c-alert__text">
              {t('if-you-get-handbook-electronic-choose-by-may-31')}
            </p>
          </Alert>
            <div className='my-u-mb--14'>
              <MyChoiceList
                variableInputName="media"
                label={t('format.text')}
                choices={mediaChoices}
                errorMessageString={t('errors.incomplete')}
                addErrorPrefix
                headingLevel='2'
              />
              <MyChoiceList
                variableInputName="language"
                label={t('language.text')}
                choices={[
                  {
                    label: t('language.english'),
                    value: 'en'
                  },
                  {
                    label: t('language.spanish'),
                    value: 'es'
                  }
                ]}
                errorMessageString={t('errors.incomplete')}
                addErrorPrefix
                headingLevel='2'
              />
              {(!hasEmail && watchMedia === EHandbookMedia.Electronic) || emailsReceivedErrors
                ? (
                    <fieldset className="ds-c-fieldset">
                      <legend className="ds-c-label">
                        <h3 className="ds-text-heading--xl">{t(`email-add.heading`)}</h3>
                      </legend>
                      <p>{t('email-add.description')}</p>
                      <MyTextField
                        name="email"
                        label={t(`email-add.label1`)}
                        labelClassName='my-u-text-transform--default'
                        defaultValue=''
                        addErrorPrefix
                        onBlur={() => methods.trigger(['email', 'confirmEmail'])}
                      />
                      <MyTextField
                        name="confirmEmail"
                        label={t(`email-add.label2`)}
                        labelClassName='my-u-text-transform--default'
                        defaultValue=''
                        addErrorPrefix
                        onBlur={() => methods.trigger(['email', 'confirmEmail'])}
                      />
                    </fieldset>
                  )
                : null}
            </div>
            {loadingState === 'error' && (
              <>
                {serverErrors?.basic
                  ? (
                  <Alert variation="error" className="my-u-mb--14">
                    <p className="ds-c-alert__text">
                      {t('errors.server-basic')}
                    </p>
                  </Alert>
                    )
                  : null}
                {serverErrors?.emailFail
                  ? (
                  <Alert variation="error" className="my-u-mb--14">
                    <p className="ds-c-alert__text">
                      {t('errors.server-email-fail')}
                    </p>
                  </Alert>
                    )
                  : null}
                {serverErrors?.emailSuccessPrefFail
                  ? (
                  <Alert variation="error" className="my-u-mb--14">
                    <p className="ds-c-alert__text">
                      {t('errors.server-email-success-pref-fail')}
                    </p>
                  </Alert>
                    )
                  : null}
              </>
            )}
            <div className="ds-c-dialog__actions">
              <div className="ds-l-form-row">
                <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                  <Button
                    type="submit"
                    className="ds-c-button ds-c-button--solid my-u-py--6 my-u-width--full"
                    disabled={isEmpty(formState.dirtyFields)}
                    siteActivityProps={{
                      core: {
                        component: 'Account Settings',
                        elementId: 'accountsettings_handbooksave_click',
                        coreClickData: {
                          linkText: t('save-changes')
                        }
                      }
                    }}
                  >
                    {loadingState === 'loading' && (
                      <MySpinner size='small' inverse isInButton/>
                    )}
                    {t('save-changes')}
                  </Button>
                </div>
                <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                  <Button
                    onClick={onClose}
                    className="ds-c-button my-u-py--6 my-u-width--full"
                    siteActivityProps={{
                      core: {
                        component: 'Account Settings',
                        elementId: 'accountsettings_handbookclose_click',
                        coreClickData: {
                          linkText: t('cancel')
                        }
                      }
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
        </>}
      </>
    </MyDialog>
  )
}
